import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  FlaskConical, 
  Users, 
  Server, 
  Activity,
  Play,
  Square,
  Plus,
  ExternalLink,
  User
} from 'lucide-react';
import { labs } from '../services/api';
import useEnvironmentStats from '../hooks/useEnvironmentStats';
import { countLabel, withEnvironmentStats, EMPTY_ENVIRONMENT_STATS } from '../utils/environmentStats';
import EnvironmentStatsStatus, { AdditionalEnvironmentStates } from '../components/EnvironmentStatsStatus';

function Dashboard() {
  const { data: labsData, isLoading: labsLoading } = useQuery({
    queryKey: ['labs'],
    queryFn: () => labs.list(),
    placeholderData: [],
    refetchInterval: 30000,
  });

  const statsQuery = useEnvironmentStats();
  const stats = statsQuery.data || EMPTY_ENVIRONMENT_STATS;
  const labsList = (labsData || []).map(lab => withEnvironmentStats(lab, stats));

  return (
    <div className="animate-fade-in">
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ marginBottom: 8 }}>Dashboard</h1>
        <p className="text-muted">
          Overview of your lab environments
        </p>
      </div>

      {/* Stats cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 32,
        }}
        role="region"
        aria-label="Dashboard statistics"
      >
        <StatCard
          icon={FlaskConical}
          label="Total Labs"
          value={stats.labCount}
          color="var(--accent-purple)"
        />
        <StatCard
          icon={Server}
          label="Registered student environments"
          value={stats.total}
          color="var(--accent-primary)"
        />
        <StatCard
          icon={Activity}
          label="Running student environments"
          value={stats.running}
          color="var(--accent-success)"
        />
        <StatCard
          icon={Square}
          label="Stopped student environments"
          value={stats.stopped}
          color="var(--accent-warning)"
        />
        <StatCard icon={Server} label="Faculty pods" value={stats.faculty?.total} color="var(--accent-secondary)" />
      </div>
      <p className="text-muted" style={{ fontSize: 13 }}>
        {stats.scope?.label || 'Lab environments'} · {countLabel(stats.pods?.total)} live managed pods.
        {' '}Each student can have an environment in several labs. Faculty pods are counted separately.
      </p>
      <AdditionalEnvironmentStates stats={stats} />
      <EnvironmentStatsStatus stats={statsQuery.data} error={statsQuery.isError} loading={statsQuery.isPending} />

      {/* Labs overview */}
      <div className="card">
        <div className="card-header">
          <h2 style={{ fontSize: 18 }}>Labs Overview</h2>
          <Link to="/labs" className="btn btn-primary btn-sm">
            <Plus size={16} />
            New Lab
          </Link>
        </div>

        {labsLoading ? (
          <div style={{ padding: 40, textAlign: 'center' }} role="status" aria-label="Loading labs">
            <div className="spinner" style={{ margin: '0 auto' }} aria-hidden="true" />
            <span className="sr-only" style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>Loading labs...</span>
          </div>
        ) : labsList.length === 0 ? (
          <div style={{
            padding: 40,
            textAlign: 'center',
            color: 'var(--text-secondary)'
          }}>
            <FlaskConical size={48} style={{ marginBottom: 16, opacity: 0.5 }} aria-hidden="true" />
            <p>No labs created yet</p>
            <Link to="/labs" className="btn btn-primary" style={{ marginTop: 16 }}>
              <Plus size={16} />
              Create Your First Lab
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {labsList.map(lab => (
              <LabRow key={lab.id} lab={lab} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="card" style={{ padding: 16 }} aria-label={`${label}: ${countLabel(value)}`}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{
            fontSize: 32,
            fontWeight: 600,
            marginBottom: 4,
            fontFamily: 'var(--font-mono)'
          }}>
            {countLabel(value)}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            {label}
          </div>
        </div>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: `${color}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }} aria-hidden="true">
          <Icon size={20} style={{ color }} />
        </div>
      </div>
    </div>
  );
}

function LabRow({ lab }) {
  const stats = lab.containerStats || EMPTY_ENVIRONMENT_STATS;

  return (
    <Link
      to={`/labs/${lab.id}`}
      aria-label={`${lab.display_name || lab.name}, ${lab.studentCount || 0} students${stats.running > 0 ? `, ${stats.running} running` : ''}${stats.stopped > 0 ? `, ${stats.stopped} stopped` : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '16px',
        background: 'var(--bg-tertiary)',
        borderRadius: 8,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'all 0.15s ease',
      }}
      onMouseOver={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
      onMouseOut={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
    >
      <div style={{
        width: 40,
        height: 40,
        borderRadius: 10,
        background: 'var(--accent-purple)20',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }} aria-hidden="true">
        <FlaskConical size={20} style={{ color: 'var(--accent-purple)' }} />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500, marginBottom: 2 }}>
          {lab.display_name || lab.name}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', gap: 16 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Users size={14} aria-hidden="true" />
            {lab.studentCount || 0} students
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <User size={14} aria-hidden="true" />
            {lab.createdBy?.name || lab.createdBy?.email || 'Unknown'}
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        {stats.running > 0 && (
          <span className="badge badge-success" aria-label={`${stats.running} running`}>
            <Activity size={12} aria-hidden="true" />
            {stats.running} running
          </span>
        )}
        {stats.stopped > 0 && (
          <span className="badge badge-warning" aria-label={`${stats.stopped} stopped`}>
            {stats.stopped} stopped
          </span>
        )}
      </div>

      <ExternalLink size={16} style={{ color: 'var(--text-muted)' }} aria-hidden="true" />
    </Link>
  );
}

export default Dashboard;

